/**
 * The state behind a VerifyWithUser card: one verdict per value, the typed
 * corrections, and the submit / skip rules.
 *
 * Shared by the ledger card (Workshop) and the M7 rail's grouped card, so both
 * rule the same way: clicking a verdict again clears it, confirming retires a
 * typed correction, "confirm all" only fills untouched rows, and nothing sends
 * until the explicit submit.
 */
import { useState } from "react";
import {
  emptyVerdict,
  formatVerifyMessage,
  isVerifyComplete,
  SKIP_VERIFY_MESSAGE,
  type VerifyRequest,
  type VerifyVerdict,
} from "./verifyWithUser";

export function useVerifyState(
  request: VerifyRequest,
  onSubmit: ((message: string) => void) | undefined,
  disabled: boolean,
  /** Rejecting is only half an answer: put the caret where the fix goes. */
  focusCorrection: (index: number) => void,
) {
  const items = request.items;
  const [verdicts, setVerdicts] = useState<VerifyVerdict[]>(() => items.map(emptyVerdict));
  const [submitted, setSubmitted] = useState(false);

  // Locked but still shown as a form: the turn is mid-flight, or the verdicts are
  // on their way and the transcript hasn't caught up yet.
  const locked = disabled || submitted || !onSubmit;
  const complete = isVerifyComplete(items, verdicts);
  const settledCount = verdicts.filter((v) => v.state !== "unset").length;
  const anyUnset = settledCount < items.length;

  const submit = () => {
    if (locked || !complete) return;
    setSubmitted(true);
    onSubmit?.(formatVerifyMessage(request, verdicts));
  };

  /** The way out. The composer stays shut until the card is settled or skipped. */
  const skip = () => {
    if (locked) return;
    setSubmitted(true);
    onSubmit?.(SKIP_VERIFY_MESSAGE);
  };

  const rule = (index: number, state: VerifyVerdict["state"]) => {
    if (locked) return;
    setVerdicts((current) =>
      current.map((verdict, i) => {
        if (i !== index) return verdict;
        // Clicking the same verdict again clears it, so a misclick on the last
        // unruled row doesn't leave the card submittable with a wrong answer.
        if (verdict.state === state) return emptyVerdict();
        // Confirming retires any correction that was typed before the change of mind.
        return { state, correction: state === "rejected" ? verdict.correction : "" };
      }),
    );
    if (state === "rejected") requestAnimationFrame(() => focusCorrection(index));
  };

  const setCorrection = (index: number, value: string) => {
    if (locked) return;
    setVerdicts((current) =>
      current.map((verdict, i) => (i === index ? { ...verdict, correction: value } : verdict)),
    );
  };

  const confirmAll = () => {
    if (locked) return;
    // Only the untouched rows: a verdict the user actually gave is never overwritten.
    setVerdicts((current) =>
      current.map((verdict) =>
        verdict.state === "unset" ? { state: "confirmed", correction: "" } : verdict,
      ),
    );
  };

  /** The next row still needing a verdict, searching forward then wrapping. */
  const nextUnruled = (from: number): number | null => {
    for (let step = 1; step <= items.length; step++) {
      const i = (from + step) % items.length;
      if ((verdicts[i]?.state ?? "unset") === "unset") return i;
    }
    return null;
  };

  const onCorrectionEnter = (index: number) => {
    if (complete) {
      submit();
      return;
    }
    const jump = nextUnruled(index);
    if (jump !== null) focusCorrection(jump);
  };

  return {
    items,
    verdicts,
    locked,
    complete,
    settledCount,
    anyUnset,
    submit,
    skip,
    rule,
    setCorrection,
    confirmAll,
    onCorrectionEnter,
  };
}
